import { useRef, useEffect, useState, useCallback } from "react";
import type { SystemChartData, GalaxyChartData, SystemChartBody } from "../types/game";
import type { SceneCommandExecutor } from "../systems/SceneCommandExecutor";

interface Props {
  system: SystemChartData | undefined;
  galaxy: GalaxyChartData | undefined;
  executor: SceneCommandExecutor | null;
  currentSystemId?: string;
}

type ChartView = "system" | "sector";

interface Tooltip {
  x: number;
  y: number;
  lines: string[];
}

const SPECTRAL_COLORS: Record<string, string> = {
  O: "#9bb0ff", B: "#aabfff", A: "#cad7ff", F: "#f8f7ff",
  G: "#fff4e0", K: "#ffd2a1", M: "#ffcc6f",
};

const PLANET_CLASS_COLORS: Record<string, string> = {
  terran: "#4ecdc4", arid: "#cc9966", oceanic: "#4488cc",
  volcanic: "#ff4422", ice: "#aaddff", gas_giant: "#cc8844",
  toxic: "#88cc44", barren: "#888888", crystalline: "#cc88ff",
};

const HIT_RADIUS = 14;

export function StarChart({ system, galaxy, executor, currentSystemId }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [view, setView] = useState<ChartView>("system");
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const [canvasSize, setCanvasSize] = useState({ w: 800, h: 600 });

  // Resize canvas to fit container
  useEffect(() => {
    const container = canvasRef.current?.parentElement;
    if (!container) return;
    const resize = () => {
      const rect = container.getBoundingClientRect();
      setCanvasSize({ w: Math.floor(rect.width), h: Math.floor(rect.height) });
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Mouse hover for tooltips
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) { setTooltip(null); return; }
    const rect = canvas.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const my = (e.clientY - rect.top) * (canvas.height / rect.height);

    if (view === "system" && system) {
      const hit = hitTestSystem(system, mx, my, canvas.width, canvas.height, executor);
      setTooltip(hit);
    } else if (view === "sector" && galaxy) {
      const hit = hitTestGalaxy(galaxy, mx, my, canvas.width, canvas.height);
      setTooltip(hit);
    } else {
      setTooltip(null);
    }
  }, [view, system, galaxy, executor]);

  // Drawing loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvasSize.w;
    canvas.height = canvasSize.h;

    let animId = 0;
    let frame = 0;
    const draw = () => {
      animId = requestAnimationFrame(draw);
      if (++frame % 3 !== 0) return; // ~20fps

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const W = canvas.width, H = canvas.height;

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "rgba(2, 4, 12, 0.95)";
      ctx.fillRect(0, 0, W, H);

      if (view === "system" && system) {
        drawSystemChart(ctx, W, H, system, executor);
      } else if (view === "sector" && galaxy) {
        drawGalaxyChart(ctx, W, H, galaxy, currentSystemId);
      }
    };

    draw();
    return () => cancelAnimationFrame(animId);
  }, [canvasSize, view, system, galaxy, executor, currentSystemId]);

  return (
    <div className="star-chart-content">
      <div className="star-chart-tabs">
        <button
          className={view === "system" ? "chart-tab-active" : ""}
          onClick={() => setView("system")}
        >
          SYSTEM
        </button>
        <button
          className={view === "sector" ? "chart-tab-active" : ""}
          onClick={() => setView("sector")}
        >
          SECTOR
        </button>
      </div>
      <div className="star-chart-canvas-wrap">
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setTooltip(null)}
          style={{ cursor: "crosshair" }}
        />
        {tooltip && (
          <div
            className="star-chart-tooltip"
            style={{ left: tooltip.x + 16, top: tooltip.y - 8 }}
          >
            {tooltip.lines.map((line, i) => (
              <div key={i} className={i === 0 ? "tt-name" : "tt-stat"}>{line}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── System chart drawing ──

function drawSystemChart(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  system: SystemChartData,
  executor: SceneCommandExecutor | null,
) {
  const CX = W / 2, CY = H / 2;
  const maxOrbital = Math.max(...system.bodies.map(b => b.orbitalRadius), 500);
  const scale = (Math.min(W, H) / 2 - 60) / maxOrbital;

  // Title
  ctx.fillStyle = "rgba(80, 130, 200, 0.7)";
  ctx.font = "bold 11px 'Courier New', monospace";
  ctx.fillText(`${system.name.toUpperCase()} SYSTEM — ${system.region}`, 12, 18);

  // Orbital rings
  for (const body of system.bodies) {
    const r = body.orbitalRadius * scale;
    ctx.beginPath();
    ctx.arc(CX, CY, r, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(30, 70, 120, 0.25)";
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  // Star at center
  const starColor = system.star.color || "#fff4e0";
  const gradient = ctx.createRadialGradient(CX, CY, 0, CX, CY, 18);
  gradient.addColorStop(0, starColor);
  gradient.addColorStop(0.5, starColor + "88");
  gradient.addColorStop(1, "transparent");
  ctx.beginPath();
  ctx.arc(CX, CY, 18, 0, Math.PI * 2);
  ctx.fillStyle = gradient;
  ctx.fill();

  // Star core
  ctx.beginPath();
  ctx.arc(CX, CY, 5, 0, Math.PI * 2);
  ctx.fillStyle = starColor;
  ctx.fill();

  ctx.fillStyle = "rgba(200, 200, 220, 0.6)";
  ctx.font = "8px 'Courier New', monospace";
  ctx.fillText(system.star.name.toUpperCase(), CX + 8, CY - 8);

  // Bodies
  for (const body of system.bodies) {
    const angle = (body.orbitalAngle * Math.PI) / 180;
    const r = body.orbitalRadius * scale;
    const bx = CX + r * Math.cos(angle);
    const by = CY + r * Math.sin(angle);

    if (body.type === "asteroid_belt") {
      // Draw scattered dots along the ring
      ctx.fillStyle = "rgba(120, 120, 120, 0.3)";
      for (let a = 0; a < Math.PI * 2; a += 0.15) {
        const jitter = (Math.sin(a * 7.3) * 0.03 + 1) * r;
        const ax = CX + jitter * Math.cos(a);
        const ay = CY + jitter * Math.sin(a);
        ctx.fillRect(ax, ay, 1.5, 1.5);
      }
      continue;
    }

    if (body.type === "jump_point") {
      // Diamond marker
      ctx.save();
      ctx.translate(bx, by);
      ctx.beginPath();
      ctx.moveTo(0, -5); ctx.lineTo(5, 0); ctx.lineTo(0, 5); ctx.lineTo(-5, 0);
      ctx.closePath();
      ctx.strokeStyle = "#4488cc";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();

      ctx.fillStyle = "#6699cc";
      ctx.font = "7px 'Courier New', monospace";
      ctx.fillText(body.name.toUpperCase(), bx + 8, by + 3);
      continue;
    }

    // Planet/station
    const planetColor = PLANET_CLASS_COLORS[body.planetClass ?? ""] ?? "#aaaaaa";
    const radius = body.type === "station" ? 3 : Math.max(3, (body.radius ?? 20) * 0.15);

    ctx.beginPath();
    ctx.arc(bx, by, radius, 0, Math.PI * 2);
    ctx.fillStyle = planetColor;
    ctx.fill();

    // Glow
    ctx.beginPath();
    ctx.arc(bx, by, radius + 2, 0, Math.PI * 2);
    ctx.strokeStyle = planetColor + "44";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Label
    ctx.fillStyle = "#aabbcc";
    ctx.font = "8px 'Courier New', monospace";
    ctx.fillText(body.name.toUpperCase(), bx + radius + 5, by + 3);

    // Moons
    if (body.moons) {
      for (const moon of body.moons) {
        const mAngle = (moon.orbitalAngle * Math.PI) / 180;
        const mScale = Math.min(scale * 0.6, 0.15); // Moons are close to parent
        const mr = moon.orbitalRadius * mScale;
        const mx = bx + mr * Math.cos(mAngle);
        const my = by + mr * Math.sin(mAngle);

        ctx.beginPath();
        ctx.arc(mx, my, 2, 0, Math.PI * 2);
        ctx.fillStyle = "#888888";
        ctx.fill();
      }
    }
  }

  // Player ship position
  if (executor) {
    const pp = executor.currentSpatialState.playerShip.position;
    const px = CX + pp.x * scale;
    const py = CY + pp.z * scale;

    // Ship marker
    ctx.save();
    ctx.translate(px, py);
    const hd = executor.currentSpatialState.playerShip.heading;
    const angle = Math.atan2(hd.z, hd.x);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(6, 0); ctx.lineTo(-3, 3); ctx.lineTo(-1, 0); ctx.lineTo(-3, -3);
    ctx.closePath();
    ctx.fillStyle = "rgba(180, 220, 255, 0.95)";
    ctx.fill();
    ctx.strokeStyle = "#aaddff";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    ctx.fillStyle = "#aaddff";
    ctx.font = "7px 'Courier New', monospace";
    ctx.fillText("YOU", px + 8, py - 4);
  }

  // Scale indicator
  const scaleUnits = maxOrbital > 2000 ? 1000 : maxOrbital > 500 ? 250 : 100;
  const scalePx = scaleUnits * scale;
  const sx = W - 20 - scalePx;
  const sy = H - 20;
  ctx.beginPath();
  ctx.moveTo(sx, sy); ctx.lineTo(sx + scalePx, sy);
  ctx.strokeStyle = "rgba(80, 130, 200, 0.5)";
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = "rgba(80, 130, 200, 0.5)";
  ctx.font = "7px 'Courier New', monospace";
  ctx.fillText(`${scaleUnits * 1000} km`, sx, sy - 4);
}

// ── Galaxy/sector chart drawing ──

function drawGalaxyChart(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  galaxy: GalaxyChartData,
  currentSystemId?: string,
) {
  const CX = W / 2, CY = H / 2;

  // Find bounds
  let maxR = 1;
  for (const s of galaxy.systems) {
    const r = Math.sqrt(s.position.x ** 2 + s.position.y ** 2);
    if (r > maxR) maxR = r;
  }
  const scale = (Math.min(W, H) / 2 - 80) / maxR;

  // Title
  ctx.fillStyle = "rgba(80, 130, 200, 0.7)";
  ctx.font = "bold 11px 'Courier New', monospace";
  ctx.fillText(`${galaxy.name.toUpperCase()} — SECTOR MAP`, 12, 18);

  // Grid
  const gridStep = maxR > 3 ? 2 : 1;
  for (let r = gridStep; r <= maxR * 1.5; r += gridStep) {
    ctx.beginPath();
    ctx.arc(CX, CY, r * scale, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(30, 70, 120, 0.15)";
    ctx.lineWidth = 0.5;
    ctx.stroke();

    ctx.fillStyle = "rgba(60, 110, 180, 0.3)";
    ctx.font = "7px 'Courier New', monospace";
    ctx.fillText(`${r} ly`, CX + r * scale + 3, CY - 2);
  }

  // Crosshair
  ctx.beginPath();
  ctx.moveTo(0, CY); ctx.lineTo(W, CY);
  ctx.moveTo(CX, 0); ctx.lineTo(CX, H);
  ctx.strokeStyle = "rgba(30, 70, 120, 0.15)";
  ctx.lineWidth = 0.5;
  ctx.stroke();

  // Systems
  for (const s of galaxy.systems) {
    const sx = CX + s.position.x * scale;
    const sy = CY - s.position.y * scale; // flip Y for screen coords
    const isCurrent = s.id === currentSystemId;
    const starColor = SPECTRAL_COLORS[s.spectralClass] ?? s.color ?? "#ffffff";

    // Star glow
    if (isCurrent) {
      const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, 20);
      grad.addColorStop(0, "rgba(100, 180, 255, 0.3)");
      grad.addColorStop(1, "transparent");
      ctx.beginPath();
      ctx.arc(sx, sy, 20, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    }

    // Star dot
    const radius = isCurrent ? 5 : 3.5;
    ctx.beginPath();
    ctx.arc(sx, sy, radius, 0, Math.PI * 2);
    ctx.fillStyle = starColor;
    ctx.fill();

    // Label
    ctx.fillStyle = isCurrent ? "#ffffff" : "#8899aa";
    ctx.font = isCurrent ? "bold 9px 'Courier New', monospace" : "8px 'Courier New', monospace";
    ctx.fillText(s.name.toUpperCase(), sx + radius + 5, sy + 3);

    // Region tag
    ctx.fillStyle = "rgba(120, 140, 180, 0.4)";
    ctx.font = "7px 'Courier New', monospace";
    ctx.fillText(s.region, sx + radius + 5, sy + 12);
  }

  // Legend
  const legendX = 12;
  let legendY = H - 70;
  ctx.fillStyle = "rgba(80, 130, 200, 0.5)";
  ctx.font = "8px 'Courier New', monospace";
  ctx.fillText("SPECTRAL CLASS", legendX, legendY);
  legendY += 12;
  for (const [cls, color] of Object.entries(SPECTRAL_COLORS)) {
    ctx.beginPath();
    ctx.arc(legendX + 4, legendY - 3, 3, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.fillStyle = "#8899aa";
    ctx.fillText(cls, legendX + 12, legendY);
    legendX; // same X
    legendY += 10;
    if (legendY > H - 10) break;
  }
}

// ── Hit testing ──

function hitTestSystem(
  system: SystemChartData,
  mx: number, my: number,
  W: number, H: number,
  executor: SceneCommandExecutor | null,
): Tooltip | null {
  const CX = W / 2, CY = H / 2;
  const maxOrbital = Math.max(...system.bodies.map(b => b.orbitalRadius), 500);
  const scale = (Math.min(W, H) / 2 - 60) / maxOrbital;

  for (const body of system.bodies) {
    if (body.type === "asteroid_belt") continue;
    const angle = (body.orbitalAngle * Math.PI) / 180;
    const r = body.orbitalRadius * scale;
    const bx = CX + r * Math.cos(angle);
    const by = CY + r * Math.sin(angle);

    if (Math.abs(mx - bx) < HIT_RADIUS && Math.abs(my - by) < HIT_RADIUS) {
      return buildBodyTooltip(body, bx, by, executor);
    }
  }

  // Check star
  if (Math.abs(mx - CX) < 12 && Math.abs(my - CY) < 12) {
    return {
      x: CX, y: CY,
      lines: [system.star.name, `Class ${system.star.spectralClass}`, system.description.slice(0, 80)],
    };
  }

  return null;
}

function buildBodyTooltip(
  body: SystemChartBody,
  x: number, y: number,
  executor: SceneCommandExecutor | null,
): Tooltip {
  const lines: string[] = [body.name];

  if (body.planetClass) lines.push(`Class: ${body.planetClass}`);
  if (body.population) lines.push(`Pop: ${body.population}`);
  if (body.faction) lines.push(body.faction);
  if (body.features?.length) lines.push(body.features.join(", "));
  if (body.description) lines.push(body.description.slice(0, 100));

  // Distance from player
  if (executor) {
    const pp = executor.currentSpatialState.playerShip.position;
    const angleRad = (body.orbitalAngle * Math.PI) / 180;
    const bpos = {
      x: body.orbitalRadius * Math.cos(angleRad),
      z: body.orbitalRadius * Math.sin(angleRad),
    };
    const dist = Math.sqrt((pp.x - bpos.x) ** 2 + (pp.z - bpos.z) ** 2);
    lines.push(`Range: ${(dist * 1000).toFixed(0)} km`);
  }

  return { x, y, lines };
}

function hitTestGalaxy(
  galaxy: GalaxyChartData,
  mx: number, my: number,
  W: number, H: number,
): Tooltip | null {
  const CX = W / 2, CY = H / 2;
  let maxR = 1;
  for (const s of galaxy.systems) {
    const r = Math.sqrt(s.position.x ** 2 + s.position.y ** 2);
    if (r > maxR) maxR = r;
  }
  const scale = (Math.min(W, H) / 2 - 80) / maxR;

  for (const s of galaxy.systems) {
    const sx = CX + s.position.x * scale;
    const sy = CY - s.position.y * scale;

    if (Math.abs(mx - sx) < HIT_RADIUS && Math.abs(my - sy) < HIT_RADIUS) {
      return {
        x: sx, y: sy,
        lines: [s.name, `Class ${s.spectralClass}`, s.region],
      };
    }
  }
  return null;
}
